import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { hasPhpBackend, checkPhpBackend, apiLogin, apiRegister, apiMe, resetBackendCheck, setBackendAvailable } from "@/lib/phpApi";

interface MockUser {
  id: string;
  username: string;
  name: string | null;
  firstName?: string | null;
  lastName?: string | null;
  role: string;
  createdAt: string;
}

const STORAGE_KEY = "rb_auth_session";
const USERS_KEY = "rb_mock_users";
const ACTIVITY_KEY = "rb_user_activity";
const API_TOKEN_KEY = "rb_api_token";

function loadUsers(): Array<{ username: string; password: string; user: MockUser }> {
  try {
    const data = localStorage.getItem(USERS_KEY);
    if (data) return JSON.parse(data);
  } catch { /* ignore */ }
  const users = [
    { username: "tasman", password: "tasman",
      user: { id: "mock_tasman", username: "tasman", name: "Tasman User", role: "user", createdAt: new Date().toISOString() } },
    { username: "admin", password: "Babajaga1!23",
      user: { id: "mock_admin", username: "admin", name: "Administrator", role: "admin", createdAt: new Date().toISOString() } },
  ];
  localStorage.setItem(USERS_KEY, JSON.stringify(users));
  return users;
}

function saveUsers(users: Array<{ username: string; password: string; user: MockUser }>) {
  localStorage.setItem(USERS_KEY, JSON.stringify(users));
}

function loadSession(): { user: MockUser } | null {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : null;
  } catch { return null; }
}

function saveSession(session: { user: MockUser } | null) {
  try {
    if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else localStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    console.error("[useAuth] Failed to save session:", err);
  }
}

export function logActivity(userId: string, username: string, action: string, details?: string) {
  try {
    const data = localStorage.getItem(ACTIVITY_KEY);
    const activities = data ? JSON.parse(data) : [];
    activities.unshift({
      id: "act_" + Math.random().toString(36).slice(2, 10),
      userId, username, action, details,
      timestamp: new Date().toISOString(),
    });
    if (activities.length > 500) activities.length = 500;
    localStorage.setItem(ACTIVITY_KEY, JSON.stringify(activities));
  } catch { /* ignore */ }
}

export function getAllActivities(): Array<Record<string, unknown>> {
  try {
    const data = localStorage.getItem(ACTIVITY_KEY);
    return data ? JSON.parse(data) : [];
  } catch { return []; }
}

export function useAuth() {
  // Load session immediately on mount — this ensures user persists across refreshes
  const [user, setUser] = useState<MockUser | null>(() => {
    try {
      const session = loadSession();
      console.log("[useAuth] init from localStorage — user:", session?.user?.username ?? "null");
      return session?.user ?? null;
    } catch { return null; }
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [backendReady, setBackendReady] = useState(hasPhpBackend());

  useEffect(() => {
    console.log("[useAuth] mount — user:", user?.username ?? "null", "backendReady:", backendReady, "token:", !!localStorage.getItem(API_TOKEN_KEY));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Check backend status on mount
  useEffect(() => {
    // If user already has a token, backend is proven reachable (login succeeded)
    // Skip the ping check to avoid false negatives from transient network issues
    if (localStorage.getItem(API_TOKEN_KEY)) {
      setBackendReady(true);
      return;
    }
    let cancelled = false;
    checkPhpBackend().then((result) => {
      if (!cancelled) {
        setBackendReady(result.available);
        if (!result.available) {
          const details = [];
          if (!result.pingOk) details.push(`ping.php: ${result.pingError}`);
          if (!result.trpcOk) details.push(`trpc: ${result.trpcError}`);
          console.warn("[useAuth] Backend check failed — " + details.join(" | "));
        }
      }
    });
    return () => { cancelled = true; };
  }, []);

  // Validate token on mount if backend available
  useEffect(() => {
    if (!backendReady) return;
    const token = localStorage.getItem(API_TOKEN_KEY);
    if (!token || user) return;
    apiMe()
      .then((apiUser) => {
        if (!apiUser || !apiUser.id) {
          console.warn("[useAuth] apiMe returned no user — keeping token, will retry on next save");
          return;
        }
        const mappedUser: MockUser = {
          id: String(apiUser.id),
          username: apiUser.username || "",
          name: apiUser.name || "",
          role: apiUser.role || "user",
          createdAt: new Date().toISOString(),
        };
        saveSession({ user: mappedUser });
        setUser(mappedUser);
      })
      .catch((err) => {
        // NEVER remove token automatically — the server may have a transient issue
        // (CORS, header stripping, rate limit, etc.) that has nothing to do with token validity.
        // Only manual logout should clear the token.
        const msg = err instanceof Error ? err.message : "";
        console.warn("[useAuth] apiMe failed (keeping token):", msg);
      });
  }, [backendReady, user]);

  // Sync across tabs (storage event) and same-tab (custom event)
  useEffect(() => {
    const storageHandler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setUser(loadSession()?.user ?? null);
      }
    };
    const loginHandler = () => {
      setUser(loadSession()?.user ?? null);
      setBackendReady(hasPhpBackend());
    };
    window.addEventListener("storage", storageHandler);
    window.addEventListener("rb-login-success", loginHandler);
    return () => {
      window.removeEventListener("storage", storageHandler);
      window.removeEventListener("rb-login-success", loginHandler);
    };
  }, []);

  const login = useCallback(async (username: string, password: string): Promise<boolean> => {
    setError("");
    setIsLoading(true);
    try {
      // ALWAYS try PHP backend login first — regardless of backendReady state.
      let phpSucceeded = false;
      let phpError = "";
      try {
        console.log("[useAuth] Trying PHP backend login for:", username);
        const result = await apiLogin(username, password);
        console.log("[useAuth] PHP login response:", result ? "got result" : "no result");
          if (result && result.user && result.token) {
          // Store token in localStorage + cookie for maximum reliability
          localStorage.setItem(API_TOKEN_KEY, result.token);
          document.cookie = `rb_api_token=${encodeURIComponent(result.token)}; path=/; max-age=${7*86400}; SameSite=Lax`;
          console.log("[useAuth] Token stored, length:", result.token.length);
          const mappedUser: MockUser = {
            id: String(result.user.id ?? "api_" + Date.now()),
            username: result.user.username || username,
            name: result.user.name || username,
            role: result.user.role || "user",
            createdAt: new Date().toISOString(),
          };
          saveSession({ user: mappedUser });
          setUser(mappedUser);
          setBackendReady(true);
          setBackendAvailable(true); // CRITICAL: backend is clearly reachable since login worked
          resetBackendCheck();
          phpSucceeded = true;
          // Notify other parts of the app that login succeeded
          window.dispatchEvent(new Event('rb-login-success'));
          setIsLoading(false);
          console.log("[useAuth] PHP login SUCCESS for:", username);
          return true;
        } else {
          phpError = "Login succeeded but no token received";
          console.warn("[useAuth] PHP login returned no token:", result);
        }
      } catch (phpErr: any) {
        phpError = phpErr instanceof Error ? phpErr.message : String(phpErr);
        console.warn("[useAuth] PHP login error:", phpError);
        // Don't fall back on wrong credentials — show the error
        if (phpError.includes("Invalid credentials") || phpError.includes("401")) {
          setError("Invalid username or password");
          setIsLoading(false);
          return false;
        }
        // Network / CORS / server errors — show diagnostic message
        const isNetworkError = phpError.includes("NetworkError") || phpError.includes("Failed to fetch") || phpError.includes("Network error") || phpError.includes("CORS") || phpError.includes("unreachable") || phpError.includes("rewritten to HTML");
        if (isNetworkError) {
          let diagnostic = "Cannot connect to authentication server.";
          if (phpError.includes("rewritten to HTML")) {
            diagnostic = "API request was intercepted by the SPA router.\n\nThis usually means:\n1. WordPress (or another CMS) in the ROOT directory is catching ALL requests.\n   → Fix: Add 'RewriteRule ^smartwater/xgi239-445/ - [L]' to your ROOT .htaccess BEFORE WordPress rules.\n2. The server is nginx — .htaccess is ignored.\n   → Fix: Add a location block in nginx for /smartwater/xgi239-445/api/ that serves .php files.\n3. Apache mod_rewrite is disabled or AllowOverride is None.\n4. PHP is not enabled on the server.\n\nOpen browser DevTools (F12) → Network tab → find the login request to see the actual response.";
          } else if (phpError.includes("Failed to fetch") || phpError.includes("Network error")) {
            diagnostic = "Network error connecting to API.\n\nPossible causes:\n1. The api/ folder was not uploaded to the server.\n2. The URL path is wrong (check browser DevTools Network tab).\n3. Server is blocking the request (firewall, CORS).";
          } else {
            diagnostic = "Cannot connect to authentication server.\n\nCheck:\n1. Browser DevTools (F12) → Network tab for the actual error.\n2. That the api/ folder exists alongside index.html.\n3. That PHP is enabled on your hosting.";
          }
          setError(diagnostic);
          setIsLoading(false);
          return false;
        }
      }

      if (phpSucceeded) {
        setIsLoading(false);
        return true;
      }

      // PHP backend is reachable but login failed — try localStorage fallback
      console.log("[useAuth] Server login failed, trying localStorage fallback...");
      const users = loadUsers();
      const found = users.find((u) => u.username === username.trim());
      if (found && found.password === password) {
        found.user.lastLoginAt = new Date().toISOString();
        saveUsers(users);
        const session = { user: found.user };
        saveSession(session);
        setUser(found.user);
        logActivity(found.user.id, found.user.username, "login", "User signed in (local fallback)");
        // Notify other parts of the app that login succeeded
        window.dispatchEvent(new Event('rb-login-success'));
        setIsLoading(false);
        return true;
      }

      // Neither server nor localStorage has matching credentials
      setError(phpError || "Invalid username or password (checked server and local)");
      setIsLoading(false);
      return false;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      setError(msg || "Login failed");
      setIsLoading(false);
      return false;
    }
  }, [backendReady]);

  const register = useCallback(async (username: string, password: string, role: string = "user"): Promise<boolean> => {
    setError("");
    setIsLoading(true);
    try {
      if (backendReady) {
        await apiRegister(username, password, username);
        setIsLoading(false);
        return true;
      }

      const users = loadUsers();
      if (users.some((u) => u.username === username)) {
        setError("Username already taken");
        setIsLoading(false);
        return false;
      }
      const newUser: MockUser = {
        id: "mock_" + Math.random().toString(36).slice(2, 10),
        username, name: username, role: role as "user" | "admin", createdAt: new Date().toISOString(),
      };
      users.push({ username, password, user: newUser });
      saveUsers(users);
      logActivity(newUser.id, newUser.username, "register", "Account created");
      setIsLoading(false);
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      setError(msg || "Registration failed");
      setIsLoading(false);
      return false;
    }
  }, [backendReady]);

  const logout = useCallback(() => {
    if (user) logActivity(user.id, user.username, "logout", "User signed out");
    saveSession(null);
    localStorage.removeItem(API_TOKEN_KEY);
    setUser(null);
    setError("");
    // Notify SessionContext to clear customer / profile so they don't bleed into the next login
    window.dispatchEvent(new Event('rb-logout'));
    window.location.hash = "#/login";
  }, [user]);

  const isAdmin = user?.role === "admin" || false;
  const isAuthenticated = !!user;

  return {
    user,
    isAuthenticated,
    isAdmin,
    isLoading,
    error,
    login,
    register,
    logout,
    refresh: () => {},
    isMock: !backendReady,
  };
}

function markBackendUnavailable() {
  try { localStorage.setItem("rb_php_backend", "0"); } catch { /* ignore */ }
}
